import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Avatar from '@mui/material/Avatar';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const fmt = (ms) => {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const MusicPicker = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chosen, setChosen] = useState(null); // full track dto
  const [startMs, setStartMs] = useState(0);
  const [previewId, setPreviewId] = useState(null);
  const [enabled, setEnabled] = useState(null); // null = unknown, false = hidden
  const audioRef = useRef(null);

  // Ask the API whether music is configured (JAMENDO_CLIENT_ID). Hide the picker if not.
  useEffect(() => {
    let active = true;
    agent.Music.config()
      .then((res) => { if (active) setEnabled(!!res.enabled); })
      .catch(() => { if (active) setEnabled(false); });
    return () => { active = false; };
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    let active = true;
    setLoading(true);
    setError('');
    const t = setTimeout(async () => {
      try {
        const res = await agent.Music.search(query.trim());
        if (active) setResults(res.tracks || []);
      } catch {
        if (active) { setResults([]); setError('Music search is unavailable right now.'); }
      } finally {
        if (active) setLoading(false);
      }
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [query]);

  const stopPreview = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPreviewId(null);
  };

  const togglePreview = (track, fromMs = 0) => {
    if (previewId === track.id && audioRef.current) { stopPreview(); return; }
    stopPreview();
    const audio = new Audio(track.audioUrl);
    audio.currentTime = fromMs / 1000;
    audio.play().catch(() => {});
    audioRef.current = audio;
    setPreviewId(track.id);
  };

  useEffect(() => () => stopPreview(), []);

  const choose = (track) => {
    setChosen(track);
    setStartMs(0);
    setResults([]);
    setQuery('');
    onSelect({
      trackId: track.id,
      url: track.audioUrl,
      name: track.name,
      artist: track.artist,
      startMs: 0,
      licenseUrl: track.licenseUrl,
      durationMs: track.durationMs,
    });
  };

  const onScrub = (_e, val) => {
    setStartMs(val);
    if (chosen) {
      onSelect({
        trackId: chosen.id,
        url: chosen.audioUrl,
        name: chosen.name,
        artist: chosen.artist,
        startMs: val,
        licenseUrl: chosen.licenseUrl,
        durationMs: chosen.durationMs,
      });
    }
  };

  // Music not configured (or config not yet known) → render nothing.
  if (!enabled) return null;

  if (chosen) {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Avatar src={chosen.imageUrl} variant="rounded" sx={{ width: 36, height: 36 }}>
            <MusicNoteIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: 'white', fontSize: '0.85rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {chosen.name}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem' }}>
              {chosen.artist}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => togglePreview(chosen, startMs)} sx={{ color: DEFAULT_THEME_COLOR }}>
            {previewId === chosen.id ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', mb: 0.5 }}>
          Start at {fmt(startMs)}
        </Typography>
        <Slider
          value={startMs}
          min={0}
          max={Math.max(0, chosen.durationMs - 1000)}
          step={500}
          onChange={onScrub}
          sx={{ color: DEFAULT_THEME_COLOR }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search music (Jamendo)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{
          mb: 1,
          '& .MuiInputBase-root': { color: 'white', background: 'rgba(255,255,255,0.05)', borderRadius: 2 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
        }}
      />
      {error && (
        <Typography sx={{ color: '#f43f5e', fontSize: '0.78rem', mb: 1 }}>{error}</Typography>
      )}
      {loading && (
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' }}>Searching…</Typography>
      )}
      <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
        {results.map((track) => (
          <Box
            key={track.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            onClick={() => choose(track)}
          >
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); togglePreview(track); }}
              sx={{ color: DEFAULT_THEME_COLOR }}
            >
              {previewId === track.id ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: 'white', fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {track.name}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>
                {track.artist} · {fmt(track.durationMs)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MusicPicker;
